import React, { useEffect, useRef, useState } from "react";
import CaseComparisonTable from "../common/caseComparisonTable";
import { Users } from "../../assets/icons";
import CaseGraph from "../common/case-graph";
import { useAppSelector } from "../../store/hooks/StoreHooks";
import html2canvas from "html2canvas";
import useComparisonFacade from "../../facade/comparison/comparison.facade";
import NoDataIcon from "../../assets/icons/NoDataFound";

const Comparison: React.FC = () => {
  const comparisonChartRef = useRef<HTMLDivElement | null>(null);
  const {
    comparisonData1,
    comparisonData2,
    comparisonData3,
    compareInput1,
    compareInput2,
    compareInput3,
  } = useAppSelector((state) => state.comparison);
  const projectDetails: any = useAppSelector(
    (state) => state?.projectTab?.tab4Input
  );
  const [showProject, setShowProject] = React.useState<boolean>(false);
  const [chart1Config, setChart1Config] = useState<any>([]);
  const [chart2Config, setChart2Config] = useState<any>([]);
  const [chart3Config, setChart3Config] = useState<any>([]);
  const { sharedCases } = useAppSelector((state) => state?.myProjects);

  const efficiencyChartRef = useRef<HTMLDivElement | null>(null);
  const {
    updateComparisonInput2,
    updateComparisonInput3,
    loadComparisonData
  } = useComparisonFacade();

  useEffect(() => {
    if (compareInput1 || compareInput2 || compareInput3) {
      if (!sharedCases?.[0]?.case?.includes(compareInput2) && compareInput2 === "") updateComparisonInput2("");
      if (!sharedCases?.[0]?.case?.includes(compareInput3) && compareInput3 === "") updateComparisonInput3("");
      loadComparisonData();
    }
  }, [compareInput2, compareInput3]);

  const { projectAndAffiliations } = useAppSelector((state) => state?.projectHeader);

  useEffect(() => {
    if (comparisonData1 && !Array.isArray(comparisonData1)) {
      if (comparisonData1.graph1Data) {
        const jsonObject = JSON.parse(comparisonData1.graph1Data);
        const data = createChart1(jsonObject);
        setChart1Config(data);
      } else {
        setChart1Config([]);
      }
    }
    if (comparisonData2 && !Array.isArray(comparisonData2)) {
      if (comparisonData2.graph1Data) {
        const graph2Data = JSON.parse(comparisonData2.graph1Data);
        const data2 = createChart1(graph2Data);
        setChart2Config(data2);
      } else {
        setChart2Config([]);
      }
    }
    if (comparisonData3 && !Array.isArray(comparisonData3)) {
      if (comparisonData3.graph1Data) {
        const graph3Data = JSON.parse(comparisonData3.graph1Data);
        const graph3Response = createChart1(graph3Data);
        setChart3Config(graph3Response);
      } else {
        setChart3Config([]);
      }
    }
  }, [comparisonData1, comparisonData2, comparisonData3]);

  const createChart1 = (chData: any) => {
    const scale = Math?.ceil(parseFloat(chData?.[3]?.[4]) / 1000);
    const xAxisMax = scale * (1000 * ((1 / scale) * (scale + 1)));

    return {
      seriesDefaults: {
        type: "scatterLine",
      },
      legend: {
        position: "bottom",
      },
      chartArea: {
        width: 900,
        height: 700,
      },
      xAxis: {
        min: 0,
        title: {
          text: `${chData?.[3]?.[0]}`,
        },
        axisCrossingValue: [0, xAxisMax, xAxisMax],
        majorGridLines: {
          width: 0.8,
          color: "#A0A0A0",
        },
        minorGridLines: {
          width: 0.3,
          color: "#A0A0A0",
          visible: true,
        },
      },
      yAxis: [
        {
          name: "Head",
          title: {
            text: `${chData?.[3]?.[1]}`,
          },
          min: 0,
          majorUnit: parseFloat(chData?.[3]?.[6]),
          color: "#0000FF",
          minorGridLines: {
            width: 0.3,
            color: "#A0A0A0",
            visible: true,
          },
          majorGridLines: {
            width: 0.8,
            color: "#A0A0A0",
          },
        },
        {
          name: "Power",
          title: {
            text: ` ${chData?.[3]?.[2]}`,
          },
          min: 0,
          max: parseFloat(chData?.[3]?.[9]),
          majorUnit: parseFloat(chData?.[3]?.[8]),
          color: "#FF0000",
        },
        {
          name: "Efficiency",
          title: {
            text: "Efficiency (%)",
          },
          min: 0,
          majorUnit: 10,
          color: "#006633",
        },
      ],
      series: [
        {
          name: "Head",
          type: "scatterLine",
          data: chData?.[0],
          color: "#0000FF",
          yAxis: "Head",
          style: "smooth",
          markers: {
            visible: false,
          },
          width: 4.0,
          tooltip: {
            visible: true,
            format: "{0} x {1} ({2:C})",
          },
        },
        {
          name: "Power",
          type: "scatterLine",
          data: chData?.[1],
          color: "#FF0000",
          yAxis: "Power",
          markers: {
            visible: false,
          },
          width: 4.0,
          style: "smooth",
        },
        {
          name: "Efficiency",
          type: "scatterLine",
          data: chData?.[2],
          color: "#006633",
          yAxis: "Efficiency",
          markers: {
            visible: false,
          },
          width: 4.0,
          style: "smooth",
        },
        {
          type: "scatterLine",
          data: chData?.[4],
          color: "#737373",
          yAxis: "Head",
          markers: {
            visible: false,
          },
          width: 2.0,
          dashType: "longDash",
          labels: {
            visible: true,
            font: "10px calibri",
            position: "bottom",
            rotation: 270,
            padding: {
              bottom: 20,
            },
          },
          visibleInLegend: true,
        },
        {
          name: "BEPFlow",
          type: "scatterLine",
          data: chData?.[5],
          color: "#006633",
          yAxis: "Head",
          markers: {
            visible: false,
          },
          width: 2.0,
          dashType: "longDash",
          labels: {
            visible: true,
            font: "10px calibri",
            position: "bottom",
            rotation: 270,
            padding: {
              bottom: 20,
            },
          },
          visibleInLegend: false,
        },
        {
          name: "MaxOperatingFlow",
          type: "scatterLine",
          data: chData?.[6],
          color: "#737373",
          yAxis: "Head",
          markers: {
            visible: false,
          },
          width: 2.0,
          dashType: "longDash",
          labels: {
            visible: true,
            font: "10px calibri",
            position: "bottom",
            rotation: 270,
            padding: {
              bottom: 20,
            },
          },
          visibleInLegend: false,
        },
        {
          name: "Target",
          type: "scatter",
          data: chData?.[7],
          color: "#4d4d4d",
          yAxis: "Head",
          markers: {
            visible: true,
            type: "square",
            background: "black",
            size: 10,
          },
          width: 2.0,
          labels: {
            visible: true,
            background: "#bfbfbf",
            border: {
              width: 1,
              color: "black",
            },
            font: "11px calibri",
          },
          tooltip: {
            visible: true,
          },
          visibleInLegend: false,
        },
        {
          name: "TargetH",
          type: "scatterLine",
          data: chData?.[8],
          color: "#bfbfbf",
          yAxis: "Head",
          markers: {
            visible: false,
          },
          width: 2.0,
          dashType: "dot",
          tooltip: {
            visible: false,
          },
          visibleInLegend: false,
        },
        {
          name: "TargetV",
          type: "scatterLine",
          data: chData?.[9],
          color: "#bfbfbf",
          yAxis: "Head",
          markers: {
            visible: false,
          },
          width: 2.0,
          dashType: "dot",
          tooltip: {
            visible: false,
          },
          visibleInLegend: false,
        },
      ],
    };
  };

  const handleMotorChartDownload = () => {
    setShowProject(true);
    setTimeout(() => {
      if (comparisonChartRef.current) {
        html2canvas(comparisonChartRef.current, {
          backgroundColor: "#253040",
        }).then((canvas) => {
          setShowProject(false);
          const imgUrl = canvas.toDataURL("image/png");

          const link = document.createElement("a");
          link.href = imgUrl;
          link.download = `${projectDetails?.projectName}.png`;
          link.click();
        });
      }
    }, 1000);
  };

  return (
    <div
      className="comparison flex flex-col gap-[16px] p-[20px] border-[1px] rounded-[8px] bg-darkcolor-600 border-darkcolor-400 w-full"
      style={{
        background:
          "var(--Dark-Mode-Gradient-Cards, linear-gradient(181deg, #253240 -16.87%, rgba(37, 48, 64, 0.40) 35.14%))",
      }}
    >
      <div className="flex flex-col gap-[16px] w-full">
        <div className="flex justify-end w-full border-b-[1px] border-darkcolor-400 pb-[16px]">
          <div
            className={
              "border-[1px] rounded-[8px] mr-3 border-darkcolor-400 cursor-pointer text-sm font-normal border-solid hover:bg-darkcolor-500 px-2 py-2 hover:border-darkcolor-300 PX-4"
            }
            onClick={() => handleMotorChartDownload()}
          >
            <button
              className={`flex justify-center items-center text-neutral-25`}
            >
              Print
            </button>
          </div>
        </div>
        <div className="p-2" ref={comparisonChartRef}>
          {showProject && (
            <div className="text-center">
              <label className="text-neutral-25">
                {projectDetails?.projectName}
              </label>
            </div>
          )}
          <CaseComparisonTable height={200} />
          <div className="flex justify-center items-center gap-[16px] w-full pt-6">
            {
              comparisonData1 && Object.values(comparisonData1)?.length > 0 &&
              <div className="w-1/3">
                <CaseGraph heading={`${projectAndAffiliations.find((item: any) => item.projectID === compareInput1)?.displayCaseName}`} chart1Data={chart1Config} />
              </div>
            }
            {
              comparisonData2 && Object.values(comparisonData2)?.length > 0 &&
              <div className="w-1/3">
                <CaseGraph heading={`${projectAndAffiliations.find((item: any) => item.projectID === compareInput2)?.displayCaseName}`} chart1Data={chart2Config} />
              </div>
            }
            {
              comparisonData3 && Object.values(comparisonData3)?.length > 0 &&
              <div className="w-1/3">
                <CaseGraph heading={`${projectAndAffiliations.find((item: any) => item.projectID === compareInput3)?.displayCaseName}`} chart1Data={chart3Config} />
              </div>
            }
          </div>
          {
            comparisonData1 && Object.values(comparisonData1)?.length > 0 &&
            <>
              <div className="text-neutral-25 m-2">{projectAndAffiliations.find((item: any) => item.projectID === compareInput1)?.displayCaseName}: Pump details</div>
              <div className={`scrollable  relative`}>
                <table
                  className={`min-w-full rounded-[8px] outline-[1px] table over`}
                  style={{ zIndex: 991 }}
                >
                  <thead style={{ zIndex: 992 }}>
                    <tr>
                      <th
                        style={{ width: "30%" }}
                        className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer text-left"
                      >
                        Pump name
                      </th>
                      <th
                        style={{ width: "14%" }}
                        className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer text-left"
                      >
                        No. of stages
                      </th>
                      <th
                        style={{ width: "14%" }}
                        className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer text-left"
                      >
                        Lift contribution
                      </th>
                      <th
                        style={{ width: "14%" }}
                        className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer text-left"
                      >
                        Power contribution
                      </th>
                      <th
                        style={{ width: "14%" }}
                        className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer text-left"
                      >
                        Intake flow
                      </th>
                      <th
                        style={{ width: "14%" }}
                        className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer text-left"
                      >
                        GVF intake
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(comparisonData1 ?? {})?.length > 0 ? (
                      <>
                        {comparisonData1?.pump && (
                          <tr>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.pump}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.pumpNoOfStages}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.pumpTDH}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.pump_TotalPower}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.pump_TFVIntake}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.pump_FreeGasIntake}
                            </td>
                          </tr>
                        )}
                        {comparisonData1?.gas_ChargePump3 && (
                          <tr>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.gas_ChargePump3}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.gas_ChargePump3_Stages}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.chargePump3FinalTDH}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.bhpChargPmp3Only ?? ""}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.chargPmp3_TFVIntake}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.fC_FreeGasIntake ?? ""}
                            </td>
                          </tr>
                        )}
                        {comparisonData1?.gas_ChargePump2 && (
                          <tr>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.gas_ChargePump2}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.gas_ChargePump2_Stages}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.chargePump2FinalTDH}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.bhpChargPmp2Only ?? ""}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.chargPmp2_TFVIntake}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.chargePump2_FreeGasIntake}
                            </td>
                          </tr>
                        )}
                        {comparisonData1?.gas_ChargePump1 && (
                          <tr>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.gas_ChargePump1}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.gas_ChargePump1_Stages}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.chargePump1FinalTDH}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.bhpChargPmp1Only ?? ""}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.chargPmp1_TFVIntake}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.chargePump1_FreeGasIntake}
                            </td>
                          </tr>
                        )}
                        {comparisonData1?.gkx && (
                          <tr>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.gkx}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.gkxNoOfStage}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.gkxFinalTDH}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.bhpGasHandlerOnly ?? ""}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.gkX_TFVIntake}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData1?.gkX_FreeGasIntake ?? ""}
                            </td>
                          </tr>
                        )}
                      </>
                    ) : (
                      <tr>
                        <td colSpan={6} className="py-10">
                          <div className="flex flex-col items-center">
                            <div className="text-neutral-25 mb-4">
                              <NoDataIcon />
                            </div>
                            <span className="text-neutral-25 title">
                              {"No data found"}
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          }
          {
            comparisonData2 && Object.values(comparisonData2)?.length > 0 &&
            <>
              <div className="text-neutral-25 m-2">{projectAndAffiliations.find((item: any) => item.projectID === compareInput2)?.displayCaseName}: Pump details</div>
              <div className={`scrollable  relative`}>
                <table
                  className={`min-w-full rounded-[8px] outline-[1px] table over`}
                  style={{ zIndex: 991 }}
                >
                  <thead style={{ zIndex: 992 }}>
                    <tr>
                      <th
                        style={{ width: "30%" }}
                        className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer text-left"
                      >
                        Pump name
                      </th>
                      <th
                        style={{ width: "14%" }}
                        className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer text-left"
                      >
                        No. of stages
                      </th>
                      <th
                        style={{ width: "14%" }}
                        className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer text-left"
                      >
                        Lift contribution
                      </th>
                      <th
                        style={{ width: "14%" }}
                        className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer text-left"
                      >
                        Power contribution
                      </th>
                      <th
                        style={{ width: "14%" }}
                        className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer text-left"
                      >
                        Intake flow
                      </th>
                      <th
                        style={{ width: "14%" }}
                        className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer text-left"
                      >
                        GVF intake
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(comparisonData2 ?? {})?.length > 0 ? (
                      <>
                        {comparisonData2?.pump && (
                          <tr>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.pump}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.pumpNoOfStages}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.pumpTDH}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2.pump_TotalPower}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.pump_TFVIntake}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.pump_FreeGasIntake}
                            </td>
                          </tr>
                        )}
                        {comparisonData2?.gas_ChargePump3 && (
                          <tr>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.gas_ChargePump3}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.gas_ChargePump3_Stages}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.chargePump3FinalTDH}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.bhpChargPmp3Only ?? ""}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.chargPmp3_TFVIntake}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.fC_FreeGasIntake ?? ""}
                            </td>
                          </tr>
                        )}
                        {comparisonData2?.gas_ChargePump2 && (
                          <tr>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.gas_ChargePump2}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.gas_ChargePump2_Stages}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.chargePump2FinalTDH}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.bhpChargPmp2Only ?? ""}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.chargPmp2_TFVIntake}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.chargePump2_FreeGasIntake}
                            </td>
                          </tr>
                        )}
                        {comparisonData2?.gas_ChargePump1 && (
                          <tr>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.gas_ChargePump1}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.gas_ChargePump1_Stages}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.chargePump1FinalTDH}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.bhpChargPmp1Only ?? ""}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.chargPmp1_TFVIntake}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.chargePump1_FreeGasIntake}
                            </td>
                          </tr>
                        )}
                        {comparisonData2?.gkx && (
                          <tr>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.gkx}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.gkxNoOfStage}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.gkxFinalTDH}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.bhpGasHandlerOnly ?? ""}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.gkX_TFVIntake}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData2?.gkX_FreeGasIntake ?? ""}
                            </td>
                          </tr>
                        )}
                      </>
                    ) : (
                      <tr>
                        <td colSpan={6} className="py-10">
                          <div className="flex flex-col items-center">
                            <div className="text-neutral-25 mb-4">
                              <NoDataIcon />
                            </div>
                            <span className="text-neutral-25 title">
                              {"No data found"}
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          }
          {
            comparisonData3 && Object.values(comparisonData3)?.length > 0 &&
            <>
              <div className="text-neutral-25 m-2">{projectAndAffiliations.find((item: any) => item.projectID === compareInput3)?.displayCaseName}: Pump details</div>
              <div className={`scrollable  relative`}>
                <table
                  className={`min-w-full rounded-[8px] outline-[1px] table over`}
                  style={{ zIndex: 991 }}
                >
                  <thead style={{ zIndex: 992 }}>
                    <tr>
                      <th
                        style={{ width: "30%" }}
                        className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer text-left"
                      >
                        Pump name
                      </th>
                      <th
                        style={{ width: "14%" }}
                        className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer text-left"
                      >
                        No. of stages
                      </th>
                      <th
                        style={{ width: "14%" }}
                        className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer text-left"
                      >
                        Lift contribution
                      </th>
                      <th
                        style={{ width: "14%" }}
                        className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer text-left"
                      >
                        Power contribution
                      </th>
                      <th
                        style={{ width: "14%" }}
                        className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer text-left"
                      >
                        Intake flow
                      </th>
                      <th
                        style={{ width: "14%" }}
                        className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer text-left"
                      >
                        GVF intake
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(comparisonData3 ?? {})?.length > 0 ? (
                      <>
                        {comparisonData3?.pump && (
                          <tr>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.pump}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.pumpNoOfStages}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.pumpTDH}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3.pump_TotalPower}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.pump_TFVIntake}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.pump_FreeGasIntake}
                            </td>
                          </tr>
                        )}
                        {comparisonData3?.gas_ChargePump3 && (
                          <tr>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.gas_ChargePump3}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.gas_ChargePump3_Stages}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.chargePump3FinalTDH}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.bhpChargPmp3Only ?? ""}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.chargPmp3_TFVIntake}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.fC_FreeGasIntake ?? ""}
                            </td>
                          </tr>
                        )}
                        {comparisonData3?.gas_ChargePump2 && (
                          <tr>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.gas_ChargePump2}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.gas_ChargePump2_Stages}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.chargePump2FinalTDH}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.bhpChargPmp2Only ?? ""}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.chargPmp2_TFVIntake}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.chargePump2_FreeGasIntake}
                            </td>
                          </tr>
                        )}
                        {comparisonData3?.gas_ChargePump1 && (
                          <tr>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.gas_ChargePump1}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.gas_ChargePump1_Stages}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.chargePump1FinalTDH}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.bhpChargPmp1Only ?? ""}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.chargPmp1_TFVIntake}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.chargePump1_FreeGasIntake}
                            </td>
                          </tr>
                        )}
                        {comparisonData3?.gkx && (
                          <tr>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.gkx}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.gkxNoOfStage}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.gkxFinalTDH}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.bhpGasHandlerOnly ?? ""}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.gkX_TFVIntake}
                            </td>
                            <td className="text-neutral-25 py-[16px] text-[14px] hover:bg-darkcolor-500 cursor-pointer">
                              {comparisonData3?.gkX_FreeGasIntake ?? ""}
                            </td>
                          </tr>
                        )}
                      </>
                    ) : (
                      <tr>
                        <td colSpan={6} className="py-10">
                          <div className="flex flex-col items-center">
                            <div className="text-neutral-25 mb-4">
                              <NoDataIcon />
                            </div>
                            <span className="text-neutral-25 title">
                              {"No data found"}
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          }
        </div>
      </div>
    </div>
  );
};
export default Comparison;
